import { DeviceManagementFilterComponent, DeviceFilterOutput } from '../../core/components/table/filter-panel/filters/device-management-filter/device-management-filter.component';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SearchComponent } from '../../core/components/search/search.component';
import { DatepickerComponent } from '../../core/components/datepicker/datepicker.component';
import { TimerComponentComponent } from '../../core/components/timer-component/timer-component.component';
import { DeviceInformationComponent } from './device-information/device-information.component';
import { CommonModule } from '@angular/common';
import { PaginationComponent } from '../../core/components/pagination/pagination.component';
import { ShowHideColumnsComponent } from '../../core/components/table/show-hide-columns/show-hide-columns.component';
import { SortComponent } from '../../core/components/sort/sort.component';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { IsNewDirective } from '../../../directives/is-new.directive';
import { DeviceSetupComponent } from '../../../device-setup/device-setup.component';
import { FirmwareUpdateModalComponent } from './firmware-update-modal/firmware-update-modal.component';
import { FirmwareUpdateComponent } from '../../../firmware-update/firmware-update.component';
import { Client, CommandStatus, ConfigurationProgressStatus, ConfigurationStatus, FirmwareUpdateCommandStatusDataOut, MeterDataOut, MeterGetAllDataIn, MeterStatus, PageInfo, PermissionsEnum, ResponseError, SortingType } from '../../../api/api-reference';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from '../../../services/auth.service';
import { SignalRService } from '../../../services/signalr.service';
import { ClipboardService } from '../../../services/clipboard.service';
import { DateRangeInput } from '../../core/models/date-range-input';
import { Column } from '../../core/models/column';
import { SelectedFilters } from '../../core/models/selectedFilers';
import { SignalRBaseComponent } from '../../../services/signalr-base.component';
import { RelayStateService } from '../../../services/relay-state.service';
import { formatDateDDMMMYYYY, processDateRange } from '../../../helper/date-utils';
import { ConfirmDialogComponent } from '../../core/components/confirm-dialog/confirm-dialog.component';
import { SetupOptionDialogComponent } from '../../core/components/setup-option-dialog/setup-option-dialog.component';

@Component({
  selector: 'app-temp',
  standalone: true,
  imports: [TranslateModule,
    SearchComponent,
    DatepickerComponent,
    TimerComponentComponent,
    DeviceInformationComponent,
    CommonModule,
    PaginationComponent,
    DeviceManagementFilterComponent,
    ShowHideColumnsComponent,
    SortComponent,
    RouterOutlet,
    IsNewDirective,
    DeviceSetupComponent,
    FirmwareUpdateModalComponent,
    FirmwareUpdateComponent,
  ConfirmDialogComponent,
  SetupOptionDialogComponent],
  templateUrl: './device-management.component.html',
  styleUrl: './device-management.component.scss',
  providers: [Client]
})
export class DeviceManagementComponent extends SignalRBaseComponent implements OnInit, OnDestroy {
  filtersApplied = 0;
  selectedFilters: SelectedFilters = {
    meterMakes: [],
    meterModels: [],
    assetTypes: [],
    configurationStatuses: [],
    serviceTypes: [],
    areas: [],
    regions: []
  };
  //#region Variables
  public currentPage: number = 1;
  public numberOfDevices: number = 0;
  public lastPageSize: number = 1;
  public pageSize: number = 10;
  public pageNumber: number = 1;
  isDeleteModalOpen = false;
  isBulkDeleteModalOpen = false;
  isBulkSetupModalOpen = false;
  isForceFullBulkSetup = false;
  isBulkSetupOptionModalOpen = false;
  isDatepickerOpen = false;
  isFirmwareUpdateModalOpen = false;
  isFirmwareUpdateProgressModalOpen = false
  selectedRow: MeterDataOut | null = null;
  public PermissionsEnum = PermissionsEnum;
  public ConfigurationStatus = ConfigurationStatus;
  isSetupModalOpen = false;
  private selection: Map<string, MeterDataOut> = new Map();
  get selectedRows(): string[] { return Array.from(this.selection.keys()); }
  get selectedMeterData(): MeterDataOut[] { return Array.from(this.selection.values()); }
  get selectedMeterModels(): string[] {
    return [...new Set(Array.from(this.selection.values()).map(r => r.meterModel).filter((m): m is string => !!m))];
  }
  public readonly CommandStatus = CommandStatus;

  data: MeterDataOut[] | undefined = [];
  // Latest pwd-change status delivered via SignalR, keyed by meterNo. Used to keep a
  // live terminal status from being clobbered by a stale snapshot returned by a table
  // reload that was issued before the async command result was persisted.
  requestData = new MeterGetAllDataIn({
    pageInfo: new PageInfo({ pageSize: this.pageSize, page: this.pageNumber }),
    search: undefined,
    startDate: undefined,
    endDate: undefined,
    sorting: SortingType.Descending,
    meterMakes: [],
    meterModels: [],
    assetTypes: [],
    configurationProgressStatus: [],
    status: undefined,
    serviceTypes: [],
    areas: [],
    regions: []
  });

  @ViewChild(DeviceManagementFilterComponent) filterPanel!: DeviceManagementFilterComponent;

  private relaySubscription?: Subscription;

  constructor(
    private client: Client,
    public toastr: ToastrService,
    public authService: AuthService,
    signalRService: SignalRService,
    public clipboardService: ClipboardService,
    private route: ActivatedRoute,
    private router: Router,
    private translate: TranslateService,
    private relayState: RelayStateService,
  ) {
    super(signalRService);

    if (this.route.snapshot.data['filterToday']) {
      const now = new Date();
      this.requestData.startDate = new Date(new Date(now).setHours(0, 0, 0, 0));
      this.requestData.endDate = new Date(new Date().setHours(23, 59, 59, 0));
      this.selectedDate = formatDateDDMMMYYYY(this.requestData.startDate);
    }
    this.selectedColumns = this.columns.filter(col => col.initial);
    this.reloadTable(1);
  }

  protected override registerHandlers(): void {
    this.registerSignalREvent('setup-device', (message) => {
      const item = this.data?.find((item) => item.id === message.meterId);
      if (item) {
        item.configurationStatus = message.status;
        if (message.meterModel) item.meterModel = message.meterModel;
      }
    });

    this.registerSignalREvent('online-status', (message) => {
      const item = this.data?.find((item) => item.id === message.meterId);
      if (item) {
        item.status = message.meterIsOnline;
        if (message.lastResponseTime) item.lastOnlineTime = message.lastResponseTime;
      }
    });

    this.registerSignalREvent('image-update', (message: FirmwareUpdateCommandStatusDataOut) => {
      this.data?.forEach((meterData) => {
        if (meterData.meterNo == message.meterNo) {
          meterData.firmwareUpdateStatus = message;
          if (message.status === CommandStatus.Success) {
            meterData.configurationStatus = ConfigurationStatus.Reconfiguring;
            this.clearSelection();
          }
        }
      });
    });

    // this.registerSignalREvent('low-level-password-change', (message: { meterNumber: string; status: CommandStatus; errorMessage?: string; errorCode?: number }) => {
    //   if (!message.meterNumber) return;
    //   status.status = message.status;
    //   status.error = message.errorMessage
    //     ? ResponseError.fromJS({ message: message.errorMessage, errorCode: message.errorCode })
    //     : undefined;
    //   // // Remember the latest pushed status so a late table reload can't regress it.
    //   // this.latestPwdChangeStatus.set(message.meterNumber, status);
    //   // this.data?.forEach((meterData) => {
    //   //   if (meterData.meterNo == message.meterNumber) {
    //   //     meterData.lowLevelPwdChangeStatus = status;
    //   //   }
    //   // });
    // });
  }

  /**
   * Refreshes subscriptions based on current page data
   */
  private async updateSignalRSubscriptions(): Promise<void> {
    if (!this.data || this.data.length === 0) return;

    // Map current page meter numbers to the SignalR groups
    const deviceNumbers = this.data
      .map(meter => meter.meterNo)
      .filter((no): no is string => !!no);

    await this.replaceDeviceSubscriptions(deviceNumbers);
  }

  reloadTable(page?: number) {
    this.pageNumber = page || 0;
    if (page) this.requestData!.pageInfo!.page = this.pageNumber;

    this.client.query(this.requestData).subscribe({
      next: (response) => {
        this.data = response.data;
        this.numberOfDevices = response.count || 0;
        this.lastPageSize = Math.ceil(this.numberOfDevices / this.pageSize);
        this.filtersApplied =
          (this.requestData.assetTypes?.length || 0) +
          (this.requestData.meterMakes?.length || 0) +
          (this.requestData.serviceTypes?.length || 0) +
          (this.requestData.meterModels?.length || 0) +
          (this.requestData.areas?.length || 0) +
          (this.requestData.regions?.length || 0) +
          (this.requestData.configurationProgressStatus?.length || 0);

        this.data?.forEach((meterData) => {
          if (meterData.meterInstallationDate) {
            meterData.meterInstallationDate = new Date(
              meterData.meterInstallationDate,
            );
          }
          // A reload issued right after triggering a pwd change can return a stale
          // snapshot (still Pending) that would overwrite a terminal status already
          // delivered live via SignalR. Keep the live terminal status in that case.
          // if (meterData.meterNo) {
          //   const live = this.latestPwdChangeStatus.get(meterData.meterNo);
          //   if (live && this.isTerminalPwdStatus(live.status) && !this.isTerminalPwdStatus(meterData.lowLevelPwdChangeStatus?.status)) {
          //     meterData.lowLevelPwdChangeStatus = live;
          //   }
          // }
        });
        this.updateSignalRSubscriptions();
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  override async ngOnInit(): Promise<void> {
    await super.ngOnInit();

    // 'disconnect-control' is owned by RelayStateService, so the row update it used to do
    // inline in registerHandlers happens here instead.
    void this.relayState.ensureSubscribed();
    this.relaySubscription = this.relayState.results$.subscribe((message) => {
      const item = this.data?.find((item) => item.id === message.meterId);
      if (item) item.isConnected = message.status;
    });
  }

  override async ngOnDestroy(): Promise<void> {
    this.relaySubscription?.unsubscribe();
    await super.ngOnDestroy();
  }
  //#endregion
  columns: Column[] = [
    { header: 'DEVICESETUP_ASSETTYPE', key: 'assetTypeName', fixPosition: false, initial: true },
    { header: 'DEVICESETUP_METERNO', key: 'meterNo', fixPosition: false, initial: true },
    { header: 'DEVICESETUP_METERIP', key: 'meterIp', fixPosition: false, initial: true },
    { header: 'DEVICESETUP_METERPORT', key: 'meterPort', fixPosition: false, initial: false },
    { header: 'DEVICESETUP_METERMAKE', key: 'meterMake', fixPosition: false, initial: true },
    { header: 'DEVICESETUP_METERTYPE', key: 'meterType', fixPosition: false, initial: true },
    { header: 'DEVICESETUP_METERPHASE', key: 'meterPhase', fixPosition: false, initial: true },
    { header: 'DEVICESETUP_METERMODEL', key: 'meterModel', fixPosition: false, initial: true },
    { header: 'DEVICESETUP_FIRMWAREVERSION', key: 'meterFirmware', fixPosition: false, initial: true },
    { header: 'DEVICESETUP_CELLULARSIMSN', key: 'cellularSimSerialNumber', fixPosition: false, initial: false },
    { header: 'DEVICESETUP_SERVICENAME', key: 'serviceName', fixPosition: false, initial: false },
    { header: 'DEVICESETUP_SERVICENUMBER', key: 'serviceNumber', fixPosition: false, initial: false },
    { header: 'DEVICES_METERINSTALLATIONDATE', key: 'meterInstallationDate', fixPosition: false, initial: false },
    { header: 'DEVICES_LASTONLINETIME', key: 'lastOnlineTime', fixPosition: false, initial: true },
    { header: 'DEVICESETUP_STATUS', key: 'status', fixPosition: true, initial: true },
    { header: 'DEVICESETUP_CONNECTION', key: 'isConnected', fixPosition: true, initial: true },
    { header: 'DEVICES_ISCONFIGURED', key: 'configurationStatus', fixPosition: true, initial: true },
    { header: 'DEVICESETUP_METERGEOLOCATION', key: 'locationChanged', fixPosition: true, initial: true },
    { header: 'DEVICESETUP_FWUPDATESTATUS', key: 'firmwareUpdateStatus', fixPosition: true, initial: true },
  ];
  selectedColumns: Column[] = [];

  //#region Getters
  get displayedColumns(): Column[] {
    const selectedKeys = new Set(this.selectedColumns.map(c => c.key));
    return this.columns.filter(col => selectedKeys.has(col.key));
  }
  //#endregion
  //#region Methods
  closeWindow() {
    this.isSetupModalOpen = false;
    this.isFirmwareUpdateModalOpen = false;
    this.reloadTable(this.currentPage);
  }
  handleRowClick(row: MeterDataOut) {
    if (!this.isDeleteModalOpen) {
      this.router.navigate(['/devices/details', row.id], { state: { row } });
    }
  }
  isAllSelected() {
    return this.data!.every((obj) => this.isRowSelected(obj.id!));
  }
  isRowSelected(id: string) {
    return this.selection.has(id);
  }
  toggleAllSelected(event: Event) {
    const checkbox = event.target as HTMLInputElement;
    if (checkbox.checked) {
      if (this.selection.size <= 100) {
        this.data!.forEach((obj) => {
          if (!this.selection.has(obj.id!)) {
            this.selection.set(obj.id!, obj);
          }
        });
      } else {
        this.toastr.warning(
          'You can select a maximum of 100 entities for bulk actions.',
        );
      }
    } else {
      this.data!.forEach((obj) => this.selection.delete(obj.id!));
    }
  }
  toggleRowSelection(row: MeterDataOut) {
    if (this.selection.has(row.id!)) {
      this.selection.delete(row.id!);
    } else {
      if (this.selection.size >= 100) {
        this.toastr.warning('You can select a maximum of 100 entities for bulk actions.');
        return;
      }
      this.selection.set(row.id!, row);
    }
  }
  clearSelection() {
    this.selection.clear();
  }
  deselectMeterById(id: string) {
    this.selection.delete(id);
  }
  handleDelete(row: MeterDataOut) {
    this.selectedRow = row;
    this.isDeleteModalOpen = true;
  }
  pageChange(value: number) {
    this.currentPage = value;
    this.reloadTable(this.currentPage);
  }
  searchChange(searchText: string) {
    this.requestData.search = searchText;
    this.clearSelection();
    this.reloadTable(1);
  }

  selectedDate: string | null = '';
  onDateRangeChanged(input: DateRangeInput): void {
    const result = processDateRange(input);

    this.requestData.startDate = result.startDate;
    this.requestData.endDate = result.endDate;
    this.selectedDate = result.selectedDate;

    this.filterDatesInTable();
  }
  filterDatesInTable() {
    this.clearSelection();
    this.client.query(this.requestData).subscribe({
      next: (response) => {
        this.data = response.data;
        this.numberOfDevices = response.count || 0;
        this.lastPageSize = Math.ceil(this.numberOfDevices / this.pageSize);
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }
  toggleModal() {
    this.isDatepickerOpen = !this.isDatepickerOpen;
  }
  clearDates(): void {
    this.requestData.startDate = undefined;
    this.requestData.endDate = undefined;
    this.clearSelection();
    this.reloadTable();
  }
  updateColumns(columns: Column[]) {
    this.selectedColumns = columns;
  }
  onSortOption(selectedSort?: SortingType) {
    if (selectedSort) this.requestData.sorting = selectedSort;
    this.clearSelection();
    this.reloadTable();
  }
  onDataReceived(event: DeviceFilterOutput) {
    this.selectedFilters = {
      meterMakes: event.meterMakes,
      meterModels: event.meterModels,
      assetTypes: event.selectedAssetTypes,
      configurationStatuses: event.selectedStatuses,
      onlineStatus: event.selectedMeterOnlineStatus,
      simStatus: event.selectedSimStatus,
      lastCommunication: event.selectedLastCommunication,
      serviceTypes: event.selectedServiceTypes,
      areas: event.areas,
      regions: event.regions
    };
    this.requestData.meterMakes = event.meterMakes;
    this.requestData.meterModels = event.meterModels;
    this.requestData.status = event.selectedMeterOnlineStatus
    this.requestData.simStatus = event.selectedSimStatus;
    this.requestData.lastCommunication = event.selectedLastCommunication;
    this.requestData.assetTypes = event.selectedAssetTypes;
    this.requestData.serviceTypes = event.selectedServiceTypes;
    this.requestData.areas = event.areas;
    this.requestData.regions = event.regions;
    this.requestData.configurationProgressStatus = event.selectedStatuses.map(
      (statusStr: string) => {
        return ConfigurationProgressStatus[
          statusStr as keyof typeof ConfigurationProgressStatus
        ];
      },
    );
    this.clearSelection();
    this.reloadTable();
  }

  retryFirmwareUpdateTrigger(row: MeterDataOut) {
    if (this.authService.isAuthorized([PermissionsEnum.Device_firmware_update])) {
      const fwUpdateStatus = row.firmwareUpdateStatus;
      this.client.retryFirmwareUpdateCommands([fwUpdateStatus.id]).subscribe({
        next: (response) => {
          this.toastr.success(response);
          this.reloadTable(this.currentPage);
        },
        error: (err) => {
          this.toastr.error(err);
        },
      });
    }
  }


  bulkSetup() {
    const rows = Array.from(this.selectedRows);
    this.client.bulkSetup(this.isForceFullBulkSetup, rows).subscribe({
      next: (r) => {
        this.toastr.success(r);
        this.clearSelection();
        this.reloadTable(this.currentPage);
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
    this.isBulkSetupModalOpen = false;
    this.isSetupModalOpen = false;
  }
  confirmBulkDelete() {
    const rows = Array.from(this.selectedRows);
    this.client.bulkDelete(rows).subscribe({
      next: (r) => {
        this.toastr.success(r);
        this.clearSelection();
        this.reloadTable(this.currentPage);
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
    this.isBulkDeleteModalOpen = false;
  }
  isCommunicationStale(row: MeterDataOut): boolean {
    return !row.lastOnlineTime || Date.now() - new Date(row.lastOnlineTime).getTime() > 24 * 60 * 60 * 1000;
  }

  getCommunicationWarning(row: MeterDataOut): string {
    const warnings: string[] = [];
    if (this.isCommunicationStale(row)) warnings.push(this.translate.instant('LAST_COMMUNICATION_STALE'));
    if (!row.ipSecConnector) warnings.push(this.translate.instant('IPSEC_CONNECTOR_OFF'));
    return warnings.join('\n');
  }

  // Session status ignores the SIM: online only if the meter responded within 3 data collection intervals (minutes).
  hasRecentCommunication(row: MeterDataOut): boolean {
    return !!row.lastOnlineTime && Date.now() - new Date(row.lastOnlineTime).getTime() <= 3 * row.dataCollectionIntervals * 60 * 1000;
  }

  getFirmwareUpdateImage(row: any): string | null {
    const status = row?.firmwareUpdateStatus?.status;

    if (
      !status ||
      status === CommandStatus.Success ||
      status === CommandStatus.Canceled
    ) {
      return null;
    }

    switch (status) {
      case CommandStatus.Scheduled:
        return 'assets/device/firmware-update-scheduled.svg';
      case CommandStatus.InProgress:
        return 'assets/device/firmware-update-inprogress.svg';
      case CommandStatus.Failed:
        return 'assets/device/firmware-update-fail.svg';
      default:
        return null;
    }
  }

  cancelDelete() {
    this.isDeleteModalOpen = false;
    this.selectedRow = null;
  }
  confirmDelete(row: MeterDataOut) {
    this.client.deleteDevice(row.id).subscribe({
      next: () => {
        this.toastr.success('Device successfully deleted.');
        this.reloadTable(this.currentPage);
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
    this.isDeleteModalOpen = false;
    this.selectedRow = null;
  }

  //#endregion
  //#region Table items details
  // The status tooltip panel is position:fixed so it escapes the horizontally
  // scrollable table wrapper's overflow clip. Place it against the trigger's
  // viewport rect, flipping above / clamping horizontally to stay on screen.
  positionStatusTooltip(event: MouseEvent): void {
    const wrap = event.currentTarget as HTMLElement;
    const panel = wrap.querySelector('.tooltip-panel') as HTMLElement | null;
    if (!panel) return;

    const rect = wrap.getBoundingClientRect();
    const gap = 8;

    let top = rect.bottom + gap;
    if (top + panel.offsetHeight > window.innerHeight) {
      top = rect.top - gap - panel.offsetHeight;
    }

    let left = rect.left + rect.width / 2 - panel.offsetWidth / 2;
    left = Math.max(gap, Math.min(left, window.innerWidth - panel.offsetWidth - gap));

    panel.style.top = `${top}px`;
    panel.style.left = `${left}px`;
  }

  getImageTooltip(status: ConfigurationStatus): string {
    if (
      status == ConfigurationStatus.InitialConfigurationFailed ||
      status == ConfigurationStatus.ReconfigurationFailed
    ) {
      return 'Device configuration failed. Click to configure.';
    }
    return 'Configuring device...';
  }
  handleSetup(row: MeterDataOut) {
    if (this.authService.isAuthorized([PermissionsEnum.Device_configure])) {
      this.selectedRow = row;
      this.isSetupModalOpen = true;
    }
  }
  getFwUpdateImageTooltip(status: FirmwareUpdateCommandStatusDataOut): string {
    const stepsString = status.steps && status.steps.length > 0
      ? status.steps.join(', ')
      : '';

    let tooltipMessage = `Status: ${status.status}\n`;
    if (status.firmwareIdentifier) tooltipMessage += `Firmware: ${status.firmwareIdentifier}\n`;
    tooltipMessage += `Attempt: ${status.retryCount} of ${status.maxRetryCount}\n`;
    if (stepsString) tooltipMessage += `Steps: ${stepsString}\n`;
    if (status.status === CommandStatus.Failed) tooltipMessage += `Error: ${status.error?.message}`;

    return tooltipMessage;
  }

  getConfigurationStatusImage(row: MeterDataOut): string | null {
    switch (row.configurationStatus) {
      case ConfigurationStatus.InitialConfigurationFailed:
      case ConfigurationStatus.ReconfigurationFailed:
        return 'assets/device/unconfigured-meter.svg';

      case ConfigurationStatus.Initializing:
      case ConfigurationStatus.Reconfiguring:
        return 'assets/device/unconfigured-meter-waiting.svg';

      default:
        return null;
    }
  }

  getLowLevelPwdChangeImage(row: any): string | null {
    const status = row?.lowLevelPwdChangeStatus?.status;

    switch (status) {
      case CommandStatus.Pending:
      case CommandStatus.Scheduled:
        return 'assets/device/password-change.svg';
      case CommandStatus.InProgress:
      case CommandStatus.Finalize:
        return 'assets/device/password-change-inprogress.svg';
      case CommandStatus.Success:
        return 'assets/device/password-change-success.svg';
      case CommandStatus.Failed:
      case CommandStatus.ErrorOccured:
        return 'assets/device/password-change-fail.svg';
      default:
        return null;
    }
  }

  // getLowLevelPwdChangeTooltip(pwdChangeStatus: LowLevelPwdChangeCommandStatusDataOut): string {
  //   switch (pwdChangeStatus?.status) {
  //     case CommandStatus.Pending:
  //     case CommandStatus.Scheduled:
  //       return this.translate.instant('DEVICE_PWDCHANGE_PENDING');
  //     case CommandStatus.InProgress:
  //     case CommandStatus.Finalize:
  //       return this.translate.instant('DEVICE_PWDCHANGE_INPROGRESS');
  //     case CommandStatus.Success:
  //       return this.translate.instant('DEVICE_PWDCHANGE_SUCCESS');
  //     case CommandStatus.Failed:
  //     case CommandStatus.ErrorOccured: {
  //       let tooltip = this.translate.instant('DEVICE_PWDCHANGE_FAILED');
  //       if (pwdChangeStatus?.error?.message) tooltip += `\n${pwdChangeStatus.error.message}`;
  //       if (this.isPwdChangeRetryable(pwdChangeStatus)) tooltip += `\n${this.translate.instant('DEVICE_PWDCHANGE_CLICKRETRY')}`;
  //       return tooltip;
  //     }
  //     default:
  //       return this.translate.instant('DEVICE_PWDCHANGE');
  //   }
  // }

  // ResponseErrorCode.Meter_NotAvailable. The API serializes this enum as its
  // numeric value (2004), not the string name the generated client declares.
  private static readonly METER_NOT_AVAILABLE_CODE = 2004;

  private isTerminalPwdStatus(status?: CommandStatus): boolean {
    return status === CommandStatus.Success || status === CommandStatus.Failed || status === CommandStatus.ErrorOccured;
  }

  // isPwdChangeRetryable(pwdChangeStatus: LowLevelPwdChangeCommandStatusDataOut): boolean {
  //   const status = pwdChangeStatus?.status;
  //   const isFailed = status === CommandStatus.Failed || status === CommandStatus.ErrorOccured;
  //   return isFailed && Number(pwdChangeStatus?.error?.errorCode) === DeviceManagementComponent.METER_NOT_AVAILABLE_CODE;
  // }

  // retryPasswordChangeTrigger(row: MeterDataOut) {
  //   if (this.authService.isAuthorized([PermissionsEnum.Device_modify])) {
  //     this.client.retryPasswordChangeCommand(row.lowLevelPwdChangeStatus.id).subscribe({
  //       next: (response) => {
  //         this.toastr.success(response);
  //         this.reloadTable(this.currentPage);
  //       },
  //       error: (err) => {
  //         this.toastr.error(err);
  //       },
  //     });
  //   }
  // }

  abortFirmwareUpdateTrigger(row: MeterDataOut) {
    if (this.authService.isAuthorized([PermissionsEnum.Device_firmware_update])) {
      const fwUpdateStatus = row.firmwareUpdateStatus;

      this.client.abortFirmwareUpdateCommands([fwUpdateStatus.id]).subscribe({
        next: (response) => {
          this.toastr.success(response);
          this.reloadTable(this.currentPage);
        },
        error: (err) => {
          this.toastr.error(err);
        },
      });
    }
    return row.firmwareUpdateStatus.status === CommandStatus.Failed
      ? 'assets/device/firmware-update-fail.svg'
      : 'assets/device/firmware-update-inprogress.svg';
  }

  closeFwUpdateModal() {
    this.isFirmwareUpdateModalOpen = false;
    this.clearSelection();
    this.reloadTable(this.currentPage);
  }

  openFirmwareUpdateProgress(row: MeterDataOut): void {
    this.selectedRow = row;
    this.isFirmwareUpdateProgressModalOpen = true;
  }

  closeFirmwareUpdateProgressModal(): void {
    this.isFirmwareUpdateProgressModalOpen = false;
    this.selectedRow = null;
    this.reloadTable(this.currentPage);
  }
}
